// Location tracking service.
//
// Uses @capacitor-community/background-geolocation when running on a real
// device (iOS / Android) and the plugin is available. Falls back to the
// browser's Geolocation API otherwise (works in Capacitor WebView too).
//
// Reports each fix to the Movorra API.

import { registerPlugin, Capacitor } from '@capacitor/core';

let BackgroundGeolocation = null;
try {
  BackgroundGeolocation = registerPlugin('BackgroundGeolocation');
} catch (e) {
  console.warn('[location] BackgroundGeolocation plugin not available, using browser fallback');
}

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';
const INTERVAL_MS =
  (parseInt(process.env.REACT_APP_LOCATION_INTERVAL_SECONDS || '15', 10)) * 1000;

let watcherId = null;
let webWatchId = null;
let authToken = null;

export function setAuthToken(token) {
  authToken = token;
}

async function reportLocation({ driverId, latitude, longitude, accuracy, speed, heading, time }) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    const res = await fetch(`${API_BASE}/api/driver-location`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userId: driverId,
        latitude,
        longitude,
        accuracyMeters: accuracy ?? null,
        speedMph: speed != null ? speed * 2.23694 : null, // m/s -> mph
        heading: heading ?? null,
        recordedAt: (time ? new Date(time) : new Date()).toISOString().replace('Z', ''),
      }),
    });
    if (!res.ok) {
      console.warn('[location] API rejected update:', res.status, await res.text());
    }
  } catch (err) {
    console.warn('[location] Network error reporting location:', err.message);
  }
}

function startBrowserGeolocation({ driverId, onLocation, onError }) {
  if (!navigator.geolocation) {
    throw new Error('Geolocation not supported');
  }
  webWatchId = navigator.geolocation.watchPosition(
    (pos) => {
      const fix = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        speed: pos.coords.speed,
        heading: pos.coords.heading,
        time: pos.timestamp,
      };
      onLocation && onLocation(fix);
      reportLocation({ driverId, ...fix });
    },
    (err) => onError && onError(err),
    { enableHighAccuracy: true, maximumAge: INTERVAL_MS, timeout: 30000 }
  );
}

export async function startTracking({ driverId, onLocation, onError }) {
  if (!driverId) throw new Error('driverId is required');

  // Try native background geolocation plugin first
  if (Capacitor.isNativePlatform() && BackgroundGeolocation) {
    try {
      watcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: 'Movorra Driver is tracking your trip.',
          backgroundTitle: 'Movorra Driver',
          requestPermissions: true,
          stale: false,
          distanceFilter: 25,
        },
        (location, error) => {
          if (error) {
            if (error.code === 'NOT_AUTHORIZED') {
              BackgroundGeolocation.openSettings();
            }
            onError && onError(error);
            return;
          }
          if (!location) return;
          const fix = {
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy,
            speed: location.speed,
            heading: location.bearing,
            time: location.time,
          };
          onLocation && onLocation(fix);
          reportLocation({ driverId, ...fix });
        }
      );
      return;
    } catch (e) {
      console.warn('[location] BackgroundGeolocation failed, falling back to browser API:', e.message);
    }
  }

  // Fallback — browser Geolocation API (works in Capacitor WebView too)
  startBrowserGeolocation({ driverId, onLocation, onError });
}

export async function stopTracking() {
  if (watcherId != null && BackgroundGeolocation) {
    try {
      await BackgroundGeolocation.removeWatcher({ id: watcherId });
    } catch (e) {
      console.warn('[location] Error removing watcher:', e.message);
    }
    watcherId = null;
    return;
  }
  if (webWatchId != null) {
    navigator.geolocation.clearWatch(webWatchId);
    webWatchId = null;
  }
}

export function isTracking() {
  return watcherId != null || webWatchId != null;
}
